import { get } from 'lodash';
import type { AnalysisRecord } from '../config/types';
import type { KpiFormula, RatioDenominator, RatioNumerator } from '../config/types';

function getField(record: AnalysisRecord, path: string): unknown {
  return get(record, path);
}

function countWhere(data: AnalysisRecord[], field: string, value: string): number {
  return data.filter((r) => getField(r, field) === value).length;
}

function countWhereIn(data: AnalysisRecord[], field: string, values: string[]): number {
  return data.filter((r) => values.includes(String(getField(r, field)))).length;
}

function countWhereNotIn(data: AnalysisRecord[], field: string, values: string[]): number {
  return data.filter((r) => !values.includes(String(getField(r, field)))).length;
}

function denominatorValue(data: AnalysisRecord[], denom: RatioDenominator): number {
  switch (denom.type) {
    case 'total':
      return data.length;
    case 'count':
      return countWhere(data, denom.field, denom.value);
    case 'countIn':
      return countWhereIn(data, denom.field, denom.values);
    case 'countNotIn':
      return countWhereNotIn(data, denom.field, denom.values);
    default:
      return 0;
  }
}

function numeratorValue(data: AnalysisRecord[], num: RatioNumerator): number {
  if (num.type === 'count') {
    return countWhere(data, num.field, num.value);
  }
  return countWhereIn(data, num.field, num.values);
}

function mode(
  data: AnalysisRecord[],
  field: string,
  excludeWhere?: { field: string; notIn: string[] },
): string {
  let subset = data;
  if (excludeWhere) {
    subset = data.filter(
      (r) => !excludeWhere.notIn.includes(String(getField(r, excludeWhere.field))),
    );
  }
  if (subset.length === 0) return '—';
  const counts: Record<string, number> = {};
  subset.forEach((r) => {
    const v = String(getField(r, field) ?? '');
    counts[v] = (counts[v] ?? 0) + 1;
  });
  let maxCount = 0;
  let result = '—';
  Object.entries(counts).forEach(([key, c]) => {
    if (c > maxCount) {
      maxCount = c;
      result = key;
    }
  });
  return result;
}

function avgDuration(data: AnalysisRecord[], startField: string, endField: string): number {
  let sum = 0;
  let count = 0;
  data.forEach((r) => {
    const s = getField(r, startField);
    const e = getField(r, endField);
    if (typeof s === 'string' && typeof e === 'string') {
      const diff = new Date(e).getTime() - new Date(s).getTime();
      if (diff > 0 && Number.isFinite(diff)) {
        sum += diff;
        count += 1;
      }
    }
  });
  return count === 0 ? 0 : sum / count / 1000;
}

export function evaluateKpi(data: AnalysisRecord[], formula: KpiFormula): number | string {
  if (formula.type === 'ratio') {
    const num = numeratorValue(data, formula.numerator);
    const denom = denominatorValue(data, formula.denominator);
    if (denom === 0) return 0;
    return num / denom;
  }
  if (formula.type === 'mode') {
    return mode(data, formula.field, formula.excludeWhere);
  }
  if (formula.type === 'count') {
    if (formula.value != null) return countWhere(data, formula.field, formula.value);
    if (formula.values != null) return countWhereIn(data, formula.field, formula.values);
    return 0;
  }
  if (formula.type === 'total') {
    return data.length;
  }
  if (formula.type === 'avg_duration') {
    return avgDuration(data, formula.startField, formula.endField);
  }
  return 0;
}
